<template>
  <div class="trail-scrubber" :style="gayaPosisi">
    <button type="button" class="scrub-btn" @click="toggle">{{ playing ? 'Pause' : 'Play' }}</button>
    <input
      class="scrub-range"
      type="range"
      :min="rentang ? rentang.start : 0"
      :max="rentang ? rentang.end : 0"
      step="1000"
      :value="waktu"
      @input="onSeek" />
    <span class="scrub-time">{{ label }}</span>
    <select class="scrub-speed form-control" v-model.number="rate">
      <option :value="1">1x</option>
      <option :value="4">4x</option>
      <option :value="16">16x</option>
    </select>
    <label class="scrub-follow">
      <input type="checkbox" v-model="follow" />
      <span>Follow</span>
    </label>
  </div>
</template>

<script>
import { publish, HOVER } from '@/util/chartSync';
import { formatDateTime } from '@/util/date';
import { timeRange } from '@/util/trailPlayback';

export default {
  name: 'TrailScrubber',
  props: {
    points: {
      type: Array,
      required: true,
    },
    massId: {
      type: [String, Number],
      default: null,
    },
    insetLeft: {
      type: Number,
      default: 0,
    },
    insetBottom: {
      type: Number,
      default: 0,
    },
    onFrame: {
      type: Function,
      default: null,
    },
    onEnd: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      waktu: 0,
      playing: false,
      rate: 1,
      follow: true,
      raf: null,
      frameTerakhir: 0,
      lebarLayar: window.innerWidth,
    };
  },
  computed: {
    // Di peta pendek kolom kontrol Leaflet turun sampai bawah, jadi minggir 62px.
    gayaPosisi() {
      const minKiri = this.lebarLayar <= 575 ? 62 : 12;
      return {
        left: `${Math.max(this.insetLeft + 12, minKiri)}px`,
        bottom: `${this.insetBottom + 26}px`,
      };
    },
    rentang() {
      return timeRange(this.points);
    },
    // Di 1x, satu jarak antar titik berlalu tiap detik.
    msPerDetik() {
      if (!this.rentang || this.points.length < 2) return 1000;
      return ((this.rentang.end - this.rentang.start) / (this.points.length - 1)) * this.rate;
    },
    // Tahun & offset dibuang; bar filter di atas sudah menyebut rentangnya.
    label() {
      if (!this.rentang) return '-';
      return formatDateTime(this.waktu, 'DD MMM HH:mm', null, false);
    },
  },
  watch: {
    // Rentang trail ganti: balik ke awal.
    points(baru, lama) {
      const sidik = (p) => (Array.isArray(p) && p.length
        ? `${p.length}:${p[0].timestamp}:${p[p.length - 1].timestamp}`
        : '');
      if (sidik(baru) === sidik(lama)) return;
      this.stop();
      this.waktu = this.rentang ? this.rentang.start : 0;
    },
  },
  mounted() {
    if (this.rentang) this.waktu = this.rentang.start;
    this._onResize = () => {
      this.lebarLayar = window.innerWidth;
    };
    window.addEventListener('resize', this._onResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this._onResize);
    this.stop();
    publish(HOVER, null);
    if (this.onEnd) this.onEnd(this.massId);
  },
  methods: {
    kirim() {
      publish(HOVER, this.waktu);
      if (this.onFrame) this.onFrame(this.massId, this.waktu, this.follow);
    },
    onSeek(event) {
      this.waktu = Number(event.target.value);
      this.kirim();
    },
    toggle() {
      if (this.playing) this.stop();
      else this.start();
    },
    start() {
      if (!this.rentang) return;
      if (this.waktu >= this.rentang.end) this.waktu = this.rentang.start;
      this.playing = true;
      this.frameTerakhir = window.performance.now();
      const langkah = (sekarang) => {
        if (!this.playing) return;
        const selisih = (sekarang - this.frameTerakhir) / 1000;
        this.frameTerakhir = sekarang;
        this.waktu = Math.min(this.waktu + selisih * this.msPerDetik, this.rentang.end);
        this.kirim();
        if (this.waktu >= this.rentang.end) {
          this.stop();
          return;
        }
        this.raf = window.requestAnimationFrame(langkah);
      };
      this.raf = window.requestAnimationFrame(langkah);
    },
    stop() {
      if (this.raf) window.cancelAnimationFrame(this.raf);
      this.raf = null;
      this.playing = false;
    },
  },
};
</script>

<style scoped>
/* left & bottom dari :style — atribusi Leaflet duduk di 17px paling bawah. */
.trail-scrubber {
  position: absolute;
  right: 12px;
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
  font-size: 12px;
}

.scrub-btn {
  border: 0;
  border-radius: 6px;
  background: #ff69b4;
  color: #ffffff;
  font-weight: 700;
  padding: 4px 14px;
  flex: 0 0 auto;
}

.scrub-range {
  flex: 1 1 auto;
  min-width: 0;
}

.scrub-time {
  flex: 0 0 auto;
  color: #374151;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.scrub-speed {
  flex: 0 0 auto;
  width: 72px;
  height: 28px;
  padding: 0 6px;
  font-size: 12px;
}

.scrub-follow {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 0;
  white-space: nowrap;
}

@media (max-width: 575px) {
  .trail-scrubber {
    flex-wrap: wrap;
    gap: 6px;
    padding: 6px 8px;
  }

  .scrub-speed {
    width: 58px;
  }

  .scrub-range {
    flex: 1 1 100%;
    order: 3;
  }
}
</style>
